import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Plus, Search, Copy, KeyRound, ChevronDown } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { LiveNowCard, BehindCard, ReportsCard } from '../components/TrainingWatch';
import { PlanReviewCard } from '../components/PlanReview';
import { sumUnits } from '../lib/sessionUnits';
import { useConfirm } from '../contexts/ConfirmContext';
import { TrainerShell } from '../components/TrainerShell';
import {
  AttendanceCard, SessionsCard, LoadCard, WeightCard, TopProgressCard, AttentionCard,
  MonthCalendar, TodaySessions, ClientsTable, MonthCard, RevenueCard,
} from '../components/DashboardParts';

import { localISODate } from '../lib/utils';
const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const opts = { withCredentials: true };

export const Dashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const [clients, setClients] = useState([]); // 1:1 clients (coached)
  const [appClients, setAppClients] = useState([]); // self-service app users
  const [group, setGroup] = useState('coached'); // 'coached' | 'app'
  const [stats, setStats] = useState(null);
  const [monthSessions, setMonthSessions] = useState([]);
  const [selectedDay, setSelectedDay] = useState(new Date().getDate());
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState('all');
  const [clientsOpen, setClientsOpen] = useState(false); // phone: the list opens with a button
  const [dialogOpen, setDialogOpen] = useState(false);
  const [credentialsModal, setCredentialsModal] = useState(null); // {name, email, link}
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    age: '',
    segment: '20-30',
    phone: '',
    gender: 'male'
  });

  useEffect(() => {
    fetchClients();
    fetchOverview();
  }, []);

  // Who's training right now (polled) and who's behind their plan this week.
  const [revenue, setRevenue] = useState(null);
  const [reports, setReports] = useState(null);
  const [live, setLive] = useState([]);
  const [behind, setBehind] = useState([]);
  const [reviewDue, setReviewDue] = useState([]);
  useEffect(() => {
    let alive = true;
    const pollLive = () => axios.get(`${API_URL}/api/live-workouts`, opts)
      .then((r) => alive && setLive(r.data)).catch(() => {});
    pollLive();
    axios.get(`${API_URL}/api/training-adherence`, opts).then((r) => alive && setBehind(r.data)).catch(() => {});
    axios.get(`${API_URL}/api/plan-review/due`, opts).then((r) => alive && setReviewDue(r.data)).catch(() => {});
    axios.get(`${API_URL}/api/revenue/weekly?weeks=8`, opts).then((r) => alive && setRevenue(r.data)).catch(() => {});
    axios.get(`${API_URL}/api/weekly-reports/status`, opts).then((r) => alive && setReports(r.data)).catch(() => {});
    const id = setInterval(() => { if (document.visibilityState === 'visible') pollLive(); }, 20000);
    const onVisible = () => document.visibilityState === 'visible' && pollLive();
    document.addEventListener('visibilitychange', onVisible);
    return () => { alive = false; clearInterval(id); document.removeEventListener('visibilitychange', onVisible); };
  }, []);

  const fetchClients = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/clients`, opts);
      setClients(response.data.filter((c) => c.access_type !== 'self_service'));
      setAppClients(response.data.filter((c) => c.access_type === 'self_service'));
    } catch (error) {
      console.error('Error fetching clients:', error);
    } finally {
      setLoading(false);
    }
  };

  // Effectiveness metrics + this month's sessions; the client list works without them.
  const fetchOverview = async () => {
    // Range covers this month plus the whole current week (it can spill into
    // the neighbouring month), so both the month and week cards are complete.
    const now = new Date();
    const iso = (d) => d.toLocaleDateString('sv-SE');
    const monday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - ((now.getDay() + 6) % 7));
    const sunday = new Date(monday.getFullYear(), monday.getMonth(), monday.getDate() + 6);
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
    const monthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    const start = iso(monday < monthStart ? monday : monthStart);
    const end = `${iso(sunday > monthEnd ? sunday : monthEnd)}T23:59:59`;
    const [statsRes, sessionsRes, googleRes] = await Promise.allSettled([
      axios.get(`${API_URL}/api/dashboard/stats`, opts),
      axios.get(`${API_URL}/api/sessions?start=${start}&end=${end}`, opts),
      axios.get(`${API_URL}/api/google-calendar/events?start=${start}&end=${end.slice(0, 10)}`, opts),
    ]);
    if (statsRes.status === 'fulfilled') setStats(statsRes.value.data);
    else console.error('Error fetching dashboard stats:', statsRes.reason);
    // Google events that aren't app sessions (title didn't match a client) are
    // shown read-only next to the sessions, marked with source: 'google'.
    const appSessions = sessionsRes.status === 'fulfilled' ? sessionsRes.value.data : [];
    const googleEvents = googleRes.status === 'fulfilled'
      ? googleRes.value.data.map((e) => ({ ...e, id: `g-${e.id}`, client_name: e.title, status: 'scheduled', source: 'google' }))
      : [];
    setMonthSessions([...appSessions, ...googleEvents]);
  };

  const handleCreateClient = async (e) => {
    e.preventDefault();
    try {
      const res = await axios.post(
        `${API_URL}/api/clients`,
        { ...formData, email: formData.email.trim() || null, age: formData.age ? parseInt(formData.age, 10) : null },
        opts
      );
      setDialogOpen(false);
      if (res.data && res.data.invite_path) {
        setCredentialsModal({
          name: res.data.name,
          email: res.data.email,
          link: `${window.location.origin}${res.data.invite_path}`
        });
      }
      setFormData({ name: '', email: '', age: '', segment: '20-30', phone: '', gender: 'male' });
      fetchClients();
      fetchOverview();
    } catch (error) {
      console.error('Error creating client:', error);
      toast.error(error.response?.data?.detail || 'Nie udało się dodać klienta');
    }
  };

  const handleResetPassword = async (e, client) => {
    e.stopPropagation();
    if (!await confirm(`Wygenerować nowy link dla ${client.name}? Poprzednie hasło i link przestaną działać.`)) {
      return;
    }
    try {
      const res = await axios.post(`${API_URL}/api/clients/${client.id}/invite`, {}, opts);
      setCredentialsModal({
        name: res.data.name,
        email: res.data.email,
        link: `${window.location.origin}${res.data.invite_path}`
      });
    } catch (error) {
      console.error('Error resetting client password:', error);
      toast.error(error.response?.data?.detail || 'Nie udało się wygenerować linku.');
    }
  };

  const openClient = (id) => navigate(`/client/${id}`);
  const today = new Date();
  const todayKey = localISODate(today);
  const rows = useMemo(() => Object.fromEntries((stats?.clients || []).map((r) => [r.client_id, r])), [stats]);
  const attentionIds = useMemo(() => new Set([...(stats?.inactive || []), ...(stats?.renew || [])].map((x) => x.client_id)), [stats]);
  const visibleClients = clients
    .filter((c) => c.name.toLowerCase().includes(query.trim().toLowerCase()))
    .filter((c) => filter === 'all' || attentionIds.has(c.id));
  const selectedDate = new Date(today.getFullYear(), today.getMonth(), selectedDay);
  const daySessions = monthSessions
    .filter((s) => s.status !== 'cancelled' && new Date(s.start_time).toDateString() === selectedDate.toDateString())
    .sort((a, b) => a.start_time.localeCompare(b.start_time));
  // Calendar-based numbers (app sessions + Google events), so the dashboard
  // reflects what's actually planned even for people not yet added as clients.
  const calendar = useMemo(() => {
    const now = new Date();
    const live = monthSessions.filter((s) => s.status !== 'cancelled');
    const monthPrefix = now.toLocaleDateString('sv-SE').slice(0, 7);
    const inMonth = live.filter((s) => s.start_time.startsWith(monthPrefix));
    const done = (s) => new Date(s.end_time) < now;
    const upcoming = live.filter((s) => !done(s)).sort((a, b) => a.start_time.localeCompare(b.start_time));
    const monday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - ((now.getDay() + 6) % 7));
    const nextMonday = new Date(monday.getFullYear(), monday.getMonth(), monday.getDate() + 7);
    const inWeek = live.filter((s) => { const t = new Date(s.start_time); return t >= monday && t < nextMonday; });
    return {
      // Counted in trainings, not calendar entries: a 2 h block is 2 (see lib/sessionUnits).
      month: {
        total: sumUnits(inMonth),
        done: sumUnits(inMonth.filter(done)),
        planned: sumUnits(inMonth.filter((s) => !done(s))),
        next: upcoming.slice(0, 4),
      },
      week: { total: sumUnits(inWeek), done: sumUnits(inWeek.filter(done)), upcoming: sumUnits(inWeek.filter((s) => !done(s))) },
    };
  }, [monthSessions]);
  const firstName = (user?.name || '').split(' ')[0];
  const weekNo = Math.ceil((((today - new Date(today.getFullYear(), 0, 1)) / 864e5) + new Date(today.getFullYear(), 0, 1).getDay() + 1) / 7);

  return (
    <TrainerShell>
      <main className="max-w-[1500px] px-4 sm:px-10 py-6 sm:py-10 flex flex-col gap-6 sm:gap-8" data-testid="dashboard-page">
        <header className="flex flex-wrap items-end justify-between gap-5">
          <div className="flex flex-col gap-3.5">
            <p className="label-text text-[var(--text-muted)]">
              {today.toLocaleDateString('pl-PL', { weekday: 'long', day: 'numeric', month: 'long' })} · tydzień {weekNo}
            </p>
            <h1 className="big-num text-[38px] sm:text-[58px]" data-testid="dashboard-heading">Dzień dobry{firstName ? `, ${firstName}` : ''}</h1>
          </div>
          <div className="flex gap-3 items-center flex-wrap">
            <label className="glass h-12 w-full sm:w-72 rounded-full flex items-center gap-2.5 px-4 text-[var(--text-muted)]">
              <Search className="w-4 h-4 shrink-0" />
              <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Szukaj klienta" aria-label="Szukaj klienta"
                className="flex-1 min-w-0 bg-transparent border-0 outline-none text-sm text-[var(--text-primary)]" data-testid="client-search" />
            </label>
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <button className="cta-primary h-12 px-6 rounded-full text-xs font-bold tracking-[0.2em] uppercase flex items-center gap-2.5" data-testid="add-client-button">
                  <Plus className="w-4 h-4" /> Dodaj klienta
                </button>
              </DialogTrigger>
              <DialogContent className="bg-[#1E1E1E] border-[#2A2A2A] rounded-3xl">
                <DialogHeader>
                  <DialogTitle className="heading-text text-2xl text-white">Nowy Klient</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCreateClient} className="space-y-6 mt-4">
                  <div>
                    <Label className="label-text text-[#A1A1A1] mb-2 block">IMIĘ I NAZWISKO</Label>
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="bg-[#0A0A0A] border-[#2A2A2A] rounded-2xl text-white"
                      required
                      data-testid="client-name-input"
                    />
                  </div>
                  <div>
                    <Label className="label-text text-[#A1A1A1] mb-2 block">EMAIL (OPCJONALNIE)</Label>
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="bg-[#0A0A0A] border-[#2A2A2A] rounded-2xl text-white"
                      placeholder="Bez e-maila klient nie dostanie jeszcze konta"
                      data-testid="client-email-input"
                    />
                  </div>
                  <div>
                    <Label className="label-text text-[#A1A1A1] mb-2 block">WIEK (OPCJONALNIE)</Label>
                    <Input
                      type="number"
                      value={formData.age}
                      onChange={(e) => setFormData({ ...formData, age: e.target.value })}
                      className="bg-[#0A0A0A] border-[#2A2A2A] rounded-2xl text-white"
                      data-testid="client-age-input"
                    />
                  </div>
                  <div>
                    <Label className="label-text text-[#A1A1A1] mb-2 block">PŁEĆ</Label>
                    <Select
                      value={formData.gender}
                      onValueChange={(value) => setFormData({ ...formData, gender: value })}
                    >
                      <SelectTrigger className="bg-[#0A0A0A] border-[#2A2A2A] rounded-2xl text-white" data-testid="client-gender-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#1E1E1E] border-[#2A2A2A] text-white">
                        <SelectItem value="male">Mężczyzna</SelectItem>
                        <SelectItem value="female">Kobieta</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="label-text text-[#A1A1A1] mb-2 block">SEGMENT</Label>
                    <Select
                      value={formData.segment}
                      onValueChange={(value) => setFormData({ ...formData, segment: value })}
                    >
                      <SelectTrigger className="bg-[#0A0A0A] border-[#2A2A2A] rounded-2xl text-white" data-testid="client-segment-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#1E1E1E] border-[#2A2A2A] text-white">
                        <SelectItem value="20-30">20-30 lat</SelectItem>
                        <SelectItem value="30-45">30-45 lat</SelectItem>
                        <SelectItem value="45+">45+ lat</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="label-text text-[#A1A1A1] mb-2 block">TELEFON (OPCJONALNIE)</Label>
                    <Input
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="bg-[#0A0A0A] border-[#2A2A2A] rounded-2xl text-white"
                      data-testid="client-phone-input"
                    />
                  </div>
                  <Button
                    type="submit"
                    className="w-full bg-[#1E1E1E] hover:bg-[#2A2A2A] text-white border border-[#C9A84C] rounded-full h-12"
                    data-testid="create-client-submit"
                  >
                    UTWÓRZ KLIENTA
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </header>

        <LiveNowCard items={live} />

        <div className="grid xl:grid-cols-[1fr_360px] gap-6 items-start">
          <div className="flex flex-col gap-5 min-w-0">
            {stats ? (
              <div className="grid md:grid-cols-3 gap-5">
                <MonthCard month={calendar.month} today={today} onOpenCalendar={() => navigate('/calendar')} />
                <SessionsCard s={calendar.week} />
                {revenue && <RevenueCard r={revenue} onOpen={() => navigate('/finanse')} />}
                <AttendanceCard att={stats.attendance} />
                <LoadCard load={stats.load} />
                <WeightCard w={stats.weight} />
                <TopProgressCard top={stats.load.top_clients} onOpen={openClient} />
                <AttentionCard title="Bez treningu 7+ dni" items={stats.inactive} empty="Wszyscy ćwiczą regularnie."
                  render={(it) => (it.days == null ? 'ponad 8 tyg.' : `${it.days} dni`)} onOpen={openClient} testid="stat-inactive" />
                <AttentionCard title="Pakiety do odnowienia" items={stats.renew} empty="Nic do odnowienia."
                  render={(it) => (it.sessions_left != null && it.sessions_left <= 2 ? `zostało ${it.sessions_left}` : `płatność ${new Date(it.payment_due_date).toLocaleDateString('pl-PL', { day: 'numeric', month: 'short' })}`)}
                  onOpen={openClient} testid="stat-renew" />
              </div>
            ) : (
              <div className="glass rounded-[28px] p-8 text-[var(--text-muted)]">Ładowanie statystyk...</div>
            )}

            <section className="glass rounded-[28px] p-5 sm:p-7">
              <div className="flex justify-between items-center gap-3 flex-wrap md:mb-2">
                <button type="button" onClick={() => setClientsOpen((o) => !o)} aria-expanded={clientsOpen || !!query}
                  className="md:hidden w-full flex items-center justify-between gap-3 text-left" data-testid="clients-toggle">
                  <span className="text-xl font-extrabold tracking-tight">Twoi klienci <span className="text-[var(--text-muted)] font-semibold">{clients.length}</span></span>
                  <ChevronDown className={`w-5 h-5 text-[var(--text-muted)] transition-transform ${clientsOpen || query ? 'rotate-180' : ''}`} />
                </button>
                <h2 className="hidden md:block text-xl font-extrabold tracking-tight" data-testid="clients-section-heading">Twoi klienci</h2>
                <div className={`${clientsOpen || query ? 'flex' : 'hidden'} md:flex gap-2 flex-wrap`}>
                  <button onClick={() => setGroup('coached')} className={`chip ${group === 'coached' ? 'nav-active' : ''}`} data-testid="group-coached">Podopieczni 1:1</button>
                  <button onClick={() => setGroup('app')} className={`chip ${group === 'app' ? 'nav-active' : ''}`} data-testid="group-app">Aplikacja (samodzielni) {appClients.length}</button>
                </div>
                <div className={`${(clientsOpen || query) && group === 'coached' ? 'flex' : 'hidden'} ${group === 'coached' ? 'md:flex' : ''} gap-2`}>
                  <button onClick={() => setFilter('all')} className={`chip ${filter === 'all' ? 'nav-active' : ''}`}>Wszyscy {clients.length}</button>
                  <button onClick={() => setFilter('attention')} className={`chip ${filter === 'attention' ? 'nav-active' : ''}`}>Wymaga uwagi {attentionIds.size}</button>
                </div>
              </div>
              <div className={`${clientsOpen || query ? '' : 'hidden'} md:block`}>
              {loading ? (
                <div className="text-center text-[var(--text-muted)] py-12">Ładowanie...</div>
              ) : group === 'app' ? (
                appClients.length === 0 ? (
                  <p className="text-[var(--text-muted)] py-10 text-center" data-testid="no-app-clients-message">Nikt jeszcze nie korzysta z aplikacji samodzielnie.</p>
                ) : (
                  <ClientsTable clients={appClients.filter((c) => c.name.toLowerCase().includes(query.trim().toLowerCase()))} rows={rows} onOpen={openClient} onReset={handleResetPassword} />
                )
              ) : clients.length === 0 ? (
                <p className="text-[var(--text-muted)] py-10 text-center" data-testid="no-clients-message">Brak klientów. Dodaj pierwszego klienta, aby rozpocząć.</p>
              ) : (
                <ClientsTable clients={visibleClients} rows={rows} onOpen={openClient} onReset={handleResetPassword} />
              )}
              </div>
            </section>
          </div>

          <aside className="flex flex-col gap-5">
            <ReportsCard status={reports} onOpen={openClient} />
            <PlanReviewCard items={reviewDue} onOpen={openClient} />
            <BehindCard items={behind} trainerName={user?.name} onOpen={openClient} />
            <MonthCalendar sessions={monthSessions} today={today} selectedDay={selectedDay} onSelectDay={setSelectedDay} key={todayKey} />
            <TodaySessions sessions={daySessions} date={selectedDate} today={today} onOpenCalendar={() => navigate('/calendar')} />
          </aside>
        </div>
      </main>

      {/* Credentials modal — shown after creating a new client account */}
      <Dialog open={!!credentialsModal} onOpenChange={(o) => !o && setCredentialsModal(null)}>
        <DialogContent className="bg-[#1E1E1E] border-[#2A2A2A] text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="heading-text text-2xl flex items-center gap-2">
              <KeyRound className="w-5 h-5 text-[#C9A84C]" />
              Link zaproszeniowy klienta
            </DialogTitle>
          </DialogHeader>
          {credentialsModal && (
            <div className="space-y-4 mt-2" data-testid="credentials-modal">
              <p className="text-sm text-[#A1A1A1]">
                Wyślij ten link do <strong className="text-white">{credentialsModal.name}</strong>. Klient sam ustawi hasło (link działa 7 dni i tylko raz). Po zamknięciu okna linku nie da się odczytać ponownie — wygenerujesz nowy.
              </p>
              <div className="space-y-3">
                <div>
                  <div className="label-text text-[#A1A1A1] text-xs mb-1">EMAIL</div>
                  <div className="flex items-center gap-2 bg-[#0A0A0A] border border-[#2A2A2A] rounded-2xl px-4 py-3">
                    <span className="text-white text-sm flex-1 select-all" data-testid="cred-email">{credentialsModal.email}</span>
                    <button
                      onClick={() => navigator.clipboard.writeText(credentialsModal.email)}
                      className="text-[#A1A1A1] hover:text-[#C9A84C]"
                      aria-label="Skopiuj email"
                    >
                      <Copy className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                <div>
                  <div className="label-text text-[#A1A1A1] text-xs mb-1">LINK DO USTAWIENIA HASŁA</div>
                  <div className="flex items-center gap-2 bg-[#0A0A0A] border border-[#C9A84C]/40 rounded-2xl px-4 py-3">
                    <span className="text-[#C9A84C] text-sm font-mono flex-1 select-all break-all" data-testid="cred-password">{credentialsModal.link}</span>
                    <button
                      onClick={() => navigator.clipboard.writeText(credentialsModal.link)}
                      className="text-[#A1A1A1] hover:text-[#C9A84C]"
                      aria-label="Skopiuj link"
                    >
                      <Copy className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
              <Button
                onClick={() => setCredentialsModal(null)}
                className="w-full bg-transparent border border-[#C9A84C] text-white hover:bg-[#C9A84C]/10 rounded-full"
                data-testid="cred-close-button"
              >
                Zamknij
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </TrainerShell>
  );
};
